import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertCircle, Eye, EyeOff, Loader2, Mail, Phone, Plus, RefreshCw, ShieldCheck, UserCheck, UserX } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { getAdminError, matchesSavedAdmin, readAdminRecord, validateAdminForm } from '../lib/admin';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const emptyForm = () => ({ name: '', email: '', phone: '', password: '', confirmPassword: '' });

const PasswordField = ({ id, label, value, onChange, disabled, describedBy }) => {
  const [visible, setVisible] = useState(false);
  const input = useRef(null);
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input ref={input} id={id} name={id} type={visible ? 'text' : 'password'} value={value}
          onChange={onChange} required autoComplete="new-password" disabled={disabled}
          aria-describedby={describedBy} className="pr-28" />
        <Button type="button" variant="ghost" size="sm"
          className="absolute right-1 top-1/2 -translate-y-1/2 h-8 px-2 text-xs"
          aria-label={`${visible ? 'Sembunyikan' : 'Tampilkan'} ${label.toLowerCase()}`}
          aria-pressed={visible} aria-controls={id} disabled={disabled}
          onMouseDown={event => event.preventDefault()}
          onClick={() => { setVisible(previous => !previous); input.current?.focus({ preventScroll: true }); }}>
          {visible ? <EyeOff className="w-4 h-4 mr-1" aria-hidden="true" /> : <Eye className="w-4 h-4 mr-1" aria-hidden="true" />}
          {visible ? 'Sembunyikan' : 'Tampilkan'}
        </Button>
      </div>
    </div>
  );
};

const Admin = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [target, setTarget] = useState(null);
  const [actionError, setActionError] = useState('');
  const [saving, setSaving] = useState(false);
  const [awaitingVerification, setAwaitingVerification] = useState(false);
  const mounted = useRef(false);
  const busy = useRef(false);
  const loadVersion = useRef(0);
  const verification = useRef(null);

  const loadAdmins = useCallback(async () => {
    const version = ++loadVersion.current;
    setLoading(true);
    setListError('');
    try {
      const response = await axios.get(`${API_URL}/api/admins`);
      if (!mounted.current || version !== loadVersion.current) return;
      if (!Array.isArray(response.data)) throw new Error('Invalid Admin list');
      const records = response.data.map(readAdminRecord);
      setAdmins(records);
      if (verification.current) {
        const { record, message } = verification.current;
        const saved = records.find(item => item.id === record.id);
        if (!matchesSavedAdmin(saved, record)) throw new Error('Saved Admin not yet verified');
        verification.current = null;
        setAwaitingVerification(false);
        toast.success(message);
      }
    } catch (error) {
      if (!mounted.current || version !== loadVersion.current) return;
      setListError(verification.current
        ? 'Perubahan telah disimpan, tetapi data terbaru belum dapat diverifikasi. Muat ulang daftar; jangan tambahkan akun yang sama lagi.'
        : getAdminError(error, 'Daftar Admin gagal dimuat. Silakan coba lagi.'));
    } finally {
      if (mounted.current && version === loadVersion.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    loadAdmins();
    return () => { mounted.current = false; loadVersion.current += 1; };
  }, [loadAdmins]);

  const changeDialog = open => {
    if (busy.current) return;
    setForm(emptyForm());
    setFormError('');
    setDialogOpen(open);
  };

  const save = async (kind, payload) => {
    if (busy.current || awaitingVerification) return;
    busy.current = true;
    setSaving(true);
    setFormError('');
    setActionError('');
    let saved = false;
    try {
      const response = kind === 'create'
        ? await axios.post(`${API_URL}/api/admins`, payload)
        : await axios.patch(`${API_URL}/api/admins/${encodeURIComponent(target.id)}`, payload);
      saved = true;
      if (!mounted.current) return;
      // Erase password fields immediately after the write, before readback.
      setForm(emptyForm());
      setDialogOpen(false);
      setTarget(null);
      const record = readAdminRecord(response.data);
      verification.current = {
        record,
        message: kind === 'create' ? 'Akun Admin berhasil dibuat.'
          : record.is_active ? 'Akun Admin berhasil diaktifkan.' : 'Akun Admin berhasil dinonaktifkan.'
      };
      setAwaitingVerification(true);
      await loadAdmins();
    } catch (error) {
      if (!mounted.current) return;
      if (saved) {
        setListError('Perubahan telah disimpan, tetapi respons tidak dapat dibaca. Muat ulang daftar sebelum melakukan perubahan lain.');
      } else if (kind === 'create') {
        // Keep only non-secret fields on failure; never log the Axios error.
        setForm(previous => ({ ...previous, password: '', confirmPassword: '' }));
        setFormError(getAdminError(error, 'Akun belum dapat dibuat. Periksa koneksi dan muat ulang daftar sebelum mencoba lagi.'));
      } else {
        setActionError(getAdminError(error, 'Status Admin gagal diubah. Silakan coba lagi.'));
      }
    } finally {
      busy.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const submit = event => {
    event.preventDefault();
    if (busy.current) return;
    const error = validateAdminForm(form);
    if (error) { setFormError(error); return; }
    save('create', { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), password: form.password });
  };
  const updateField = name => event => setForm(previous => ({ ...previous, [name]: event.target.value }));
  const disabled = saving || loading || awaitingVerification;
  const activeCount = admins.filter(admin => admin.is_active).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Manajemen Admin</h1>
          <p className="text-muted-foreground mt-1">Buat dan kelola akses Admin untuk membantu operasional kos.</p>
        </div>
        <Button onClick={() => changeDialog(true)} disabled={disabled} className="rounded-xl shrink-0" data-testid="add-admin-btn">
          <Plus className="w-4 h-4 mr-2" aria-hidden="true" />Tambah Admin
        </Button>
      </div>

      <Card className="p-4 sm:p-5 bg-primary/5 border-primary/20">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
          <div className="min-w-0 text-sm space-y-1">
            <p className="font-semibold">Akses operasional, kendali tetap di pemilik</p>
            <p className="text-muted-foreground">Admin dapat mengelola kamar, penghuni, pembayaran, laporan, dan operasional kos. Hanya pemilik yang dapat membuat atau menonaktifkan Admin dan mengubah rekening bank.</p>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 sm:p-5 border-b">
          <div>
            <h2 className="font-heading font-semibold">Daftar Admin</h2>
            {!loading && !listError && <p className="text-sm text-muted-foreground">{admins.length} akun · {activeCount} aktif · {admins.length - activeCount} nonaktif</p>}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={loadAdmins} disabled={loading || saving}>
            <RefreshCw className="w-4 h-4 mr-2" aria-hidden="true" />Muat ulang
          </Button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-10 text-muted-foreground" role="status">
            <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />Memuat Admin…
          </div>
        ) : listError ? (
          <div className="p-6 sm:p-10 text-center space-y-4">
            <AlertCircle className="w-8 h-8 text-destructive mx-auto" aria-hidden="true" />
            <p className="text-sm text-destructive" role="alert">{listError}</p>
            <Button variant="outline" onClick={loadAdmins} disabled={saving}>Coba lagi</Button>
          </div>
        ) : admins.length === 0 ? (
          <div className="p-8 sm:p-12 text-center space-y-3" data-testid="admin-empty-state">
            <ShieldCheck className="w-10 h-10 text-muted-foreground mx-auto" aria-hidden="true" />
            <h3 className="font-semibold">Belum ada Admin</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">Akun Admin dibuat oleh pemilik. Tambahkan orang yang Anda percaya untuk membantu mengelola kos.</p>
          </div>
        ) : (
          <ul className="divide-y" aria-label="Daftar akun Admin">
            {admins.map(admin => (
              <li key={admin.id} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`admin-row-${admin.id}`}>
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold break-all">{admin.name}</h3>
                    <Badge variant="outline">Admin</Badge>
                    <Badge className={admin.is_active ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-muted text-muted-foreground border-border'} data-testid={`admin-status-${admin.id}`}>
                      {admin.is_active ? 'Aktif' : 'Nonaktif'}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground flex items-start gap-2"><Mail className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" /><span className="break-all">{admin.email}</span></p>
                  {admin.phone && <p className="text-sm text-muted-foreground flex items-start gap-2"><Phone className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" /><span className="break-all">{admin.phone}</span></p>}
                </div>
                <Button variant="outline" className={`w-full sm:w-auto shrink-0 ${admin.is_active ? 'text-destructive' : ''}`}
                  disabled={disabled} aria-label={`${admin.is_active ? 'Nonaktifkan' : 'Aktifkan'} Admin ${admin.name}`}
                  onClick={() => { setActionError(''); setTarget(admin); }}>
                  {admin.is_active ? <UserX className="w-4 h-4 mr-2" aria-hidden="true" /> : <UserCheck className="w-4 h-4 mr-2" aria-hidden="true" />}
                  {admin.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={changeDialog}>
        {dialogOpen && <DialogContent className="w-[calc(100%-2rem)] max-w-lg max-h-[90dvh] overflow-y-auto rounded-xl p-5 sm:p-6">
          <DialogHeader className="text-left pr-5">
            <DialogTitle>Tambah Admin</DialogTitle>
            <DialogDescription>Akun ini hanya memiliki role Admin untuk operasional kos, bukan pemilik. Bagikan email dan password secara pribadi kepada Admin.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" aria-label="Form tambah Admin" autoComplete="off">
            <div className="space-y-2">
              <Label htmlFor="admin-name">Nama Admin</Label>
              <Input id="admin-name" name="admin-name" value={form.name} onChange={updateField('name')} required disabled={saving} autoComplete="off" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-email">Email</Label>
              <Input id="admin-email" name="admin-email" type="email" value={form.email} onChange={updateField('email')} required disabled={saving} autoComplete="off" autoCapitalize="none" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-phone">Nomor telepon <span className="text-muted-foreground font-normal">(opsional)</span></Label>
              <Input id="admin-phone" name="admin-phone" type="tel" value={form.phone} onChange={updateField('phone')} disabled={saving} autoComplete="off" />
            </div>
            <PasswordField id="admin-password" label="Password" value={form.password} onChange={updateField('password')} disabled={saving} describedBy="admin-password-help" />
            <p id="admin-password-help" className="text-xs text-muted-foreground">Minimal 8 karakter, maksimal 72 byte UTF-8. Password tidak akan ditampilkan kembali setelah akun dibuat.</p>
            <PasswordField id="admin-confirm-password" label="Konfirmasi password" value={form.confirmPassword} onChange={updateField('confirmPassword')} disabled={saving} />
            {formError && <p className="text-sm text-destructive" role="alert">{formError}</p>}
            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" disabled={saving} onClick={() => changeDialog(false)}>Batal</Button>
              <Button type="submit" disabled={saving} data-testid="save-admin-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}{saving ? 'Menyimpan…' : 'Buat Admin'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>}
      </Dialog>

      <AlertDialog open={!!target} onOpenChange={open => { if (!open && !busy.current) setTarget(null); }}>
        <AlertDialogContent className="w-[calc(100%-2rem)] max-w-lg max-h-[90dvh] overflow-y-auto rounded-xl">
          <AlertDialogHeader className="text-left">
            <AlertDialogTitle>{target?.is_active ? 'Nonaktifkan Admin?' : 'Aktifkan Admin?'}</AlertDialogTitle>
            <AlertDialogDescription className="break-words">
              <span className="block font-medium text-foreground break-all">{target?.name}</span>
              <span className="block break-all mb-3">{target?.email}</span>
              {target?.is_active ? 'Admin ini akan kehilangan akses ke aplikasi. Akun dan riwayat operasionalnya tetap tersimpan.' : 'Admin ini akan dapat masuk kembali dan mengelola operasional kos.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {actionError && <p className="text-sm text-destructive" role="alert">{actionError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Batal</AlertDialogCancel>
            <AlertDialogAction disabled={saving} onClick={event => { event.preventDefault(); if (target) save('status', { is_active: !target.is_active }); }}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
              {saving ? 'Menyimpan…' : target?.is_active ? 'Ya, nonaktifkan' : 'Ya, aktifkan'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Admin;
